import { getMoscowDate, moscowDateToUtcRange } from '@/pages/report/utils/get-moscow-date.function';
import { createBaseQueryWith401Retry } from '@/redux/api/rtk-query-api';
import { API_SDK_URL, VITE_ANALYTICS_PARSE_API_URL, VITE_BM_API_BASE_URL, VITE_PARSE_API_URL } from '@env';
import { createApi } from '@reduxjs/toolkit/query/react';
import groupBy from 'lodash/groupBy';

export type ProductCarouselAllListType = {
  anchorArticle: number;
  timestamp: string;
  parseMethod: number;
  campaignWbId: number | null;
  position: number;
  boostPosition: number | null;
  cpm: number | null;
}[];

export interface ProductCarouselType {
  dailystats: ProductCarouselDailystatType[];
  anchorArticle: number;
}

export interface ProductCarouselDailystatType {
  anchorArticle: number;
  article: number;
  timestamp: string;
  date: string;
  parseMethod: number;
  campaignWbId: number;
  position: number;
  boostPosition: number;
  cpm: number;
}

export interface UnknownProductOut {
  article: number;
  imageThumbUrl?: string;
  imageUrl?: string;
  name: string;
  brandName: string;
  subjectWbId?: number;
  kindWbId?: number;
  accountWbId?: number;
  accountTrademark?: string;
  price: number;
  sppPrice: number;
  rating: number;
  feedbackCount: number;
}

export interface ProductSizeDailystatsSeoParams {
  articles: number[];
  minTimestamp: Date;
  maxTimestamp: Date;
}

export interface ProductSizeDailystatSeo {
  timestamp: string;
  article: number;
  sizeWbId: number;
  orderCount?: number;
  revenueSum?: number;
}

export const productCarouselApi = createApi({
  reducerPath: 'productCarouselApi',
  baseQuery: createBaseQueryWith401Retry(`${VITE_PARSE_API_URL}/`),

  endpoints: (builder) => ({
    getProductCarousels: builder.query<
      {
        productCarouselByArticle: Map<number, ProductCarouselType>;
      },
      { articles: number[] | string[]; date?: string; group?: boolean }
    >({
      query: ({ articles, date, group }) => {
        const params = new URLSearchParams();

        for (const a of articles) params.append('articles', String(a));
        params.set('campaignWbId', '1');
        params.set('group', String(group));

        if (date?.trim()) {
          const { minTimestamp, maxTimestampExclusive } = moscowDateToUtcRange(date);
          params.set('minTimestamp', minTimestamp);
          params.set('maxTimestamp', maxTimestampExclusive);
        }

        return `/product_carousel/product_carousels/all?${params.toString()}`;
      },
      transformResponse: (_result: ProductCarouselAllListType, _, queryArgs) => {
        const result = _result[0];
        const article = queryArgs.articles[0];
        const productCarouselByArticle = new Map<number, ProductCarouselType>();

        const groupedDailystatRows = groupBy(
          result,
          // @ts-ignore
          ([, productArticle]) =>
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            productArticle,
        );

        const groupedCarouselRows = Object.entries(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          groupBy(result, ([, productArticle]) => productArticle),
        );

        for (const [productArticle] of groupedCarouselRows) {
          const dailystatRows = groupedDailystatRows[+productArticle];

          const dailystats: ProductCarouselDailystatType[] = dailystatRows
            ? dailystatRows.map(
                ([
                  anchorArticle,
                  article,
                  timestamp,
                  parseMethod,
                  campaignWbId,
                  position,
                  boostPosition,
                  cpm,
                ]) => ({
                  anchorArticle,
                  article,
                  timestamp,
                  date: getMoscowDate(new Date(timestamp)),
                  parseMethod,
                  campaignWbId: campaignWbId || 0,
                  position,
                  boostPosition: boostPosition || 0,
                  cpm: cpm || 0,
                }),
              )
            : [];

          const product = {
            dailystats,
            anchorArticle: +article,
          };

          if (!productCarouselByArticle.has(+productArticle)) {
            productCarouselByArticle.set(+productArticle, product);
          }
        }

        return {
          productCarouselByArticle,
        };
      },
    }),
    getFboOrders: builder.query<any, { articles: number[] }>({
      query: (payload) => ({
        url: `${VITE_ANALYTICS_PARSE_API_URL}/card/cards`,
        method: 'POST',
        body: payload,
      }),
    }),
    getProductSizeDailystatsSeo: builder.query<
      ProductSizeDailystatSeo[],
      ProductSizeDailystatsSeoParams
    >({
      query: (payload) => ({
        url: `${VITE_BM_API_BASE_URL}/sales/product_size_dailystat/union/seo`,
        method: 'POST',
        body: payload,
      }),
    }),
    getProductInfo: builder.query<UnknownProductOut[], { articles: number[] }>({
      query: ({ articles }) => ({
        url: `${VITE_BM_API_BASE_URL}/bm/product/products/unknowns`,
        method: 'POST',
        body: { articles },
      }),
    }),
    updateDailyStat: builder.mutation<
      ProductCarouselDailystatType[],
      {
        accountArticles: { accountWbId: number; article: number }[];
        anchorArticle: number;
        depth: number;
      }
    >({
      query: (payload) => ({
        url: '/product_carousel/product_carousels/dailystat',
        method: 'PATCH',
        body: payload,
      }),
    }),
  }),
});

export const {
  useLazyGetProductCarouselsQuery,
  useLazyGetProductInfoQuery,
  useLazyGetFboOrdersQuery,
  useLazyGetProductSizeDailystatsSeoQuery,
  useUpdateDailyStatMutation,
} = productCarouselApi;
